/**
 * .novacad project files — self-contained JSON: the parametric timeline plus
 * every referenced mesh asset (base64), so a file opened on another machine
 * (or after clearing the browser) rebuilds completely. The recipe itself is
 * tiny; imported meshes dominate the size.
 */

import type { TimelineJSON } from '../timeline/timeline';
import type { ImportMeshParams, ImportStepParams, PartParams } from '../timeline/types';
import { db } from './db';

export interface NovacadFile {
  format: 'novacad';
  version: 1;
  name: string;
  savedAt: number;
  timeline: TimelineJSON;
  /** `kind` is optional for back-compat: old files are all mesh assets. */
  assets: Record<string, { name: string; mimeType: string; data: string; kind?: 'mesh' | 'brep' }>;
  /** Assembly sources (recursively), keyed by project id — self-contained. */
  parts?: Record<string, { name: string; timeline: TimelineJSON }>;
}

function referencedAssetHashes(timeline: TimelineJSON): string[] {
  const hashes = new Set<string>();
  for (const f of timeline.features) {
    if (f.type === 'import-mesh') hashes.add((f.params as ImportMeshParams).assetHash);
    if (f.type === 'import-step') hashes.add((f.params as ImportStepParams).assetHash);
  }
  return [...hashes];
}

function referencedPartIds(timeline: TimelineJSON): string[] {
  const ids = new Set<string>();
  for (const f of timeline.features) {
    if (f.type === 'part') ids.add((f.params as PartParams).projectId);
  }
  return [...ids];
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve((r.result as string).split(',')[1] ?? '');
    r.onerror = () => reject(r.error);
    r.readAsDataURL(blob);
  });
}

async function base64ToBlob(b64: string, type: string): Promise<Blob> {
  return (await fetch(`data:${type};base64,${b64}`)).blob();
}

export async function buildProjectFile(name: string, timeline: TimelineJSON): Promise<Blob> {
  const assets: NovacadFile['assets'] = {};
  const parts: NonNullable<NovacadFile['parts']> = {};

  // Walk timelines recursively: this project's assets + every assembly
  // source project (and THEIR parts/assets), so the file stands alone.
  const queue: TimelineJSON[] = [timeline];
  const seenParts = new Set<string>();
  while (queue.length > 0) {
    const t = queue.shift()!;
    for (const hash of referencedAssetHashes(t)) {
      if (assets[hash]) continue;
      const rec = await db.assets.get(hash);
      if (rec) {
        assets[hash] = {
          name: rec.name,
          mimeType: rec.mimeType,
          data: await blobToBase64(rec.data),
          ...(rec.kind === 'brep' ? { kind: 'brep' as const } : {}),
        };
      }
    }
    for (const pid of referencedPartIds(t)) {
      if (seenParts.has(pid)) continue;
      seenParts.add(pid);
      const rec = await db.projects.get(pid);
      if (rec) {
        parts[pid] = { name: rec.name, timeline: rec.timeline };
        queue.push(rec.timeline);
      }
    }
  }

  const file: NovacadFile = {
    format: 'novacad',
    version: 1,
    name,
    savedAt: Date.now(),
    timeline,
    assets,
    ...(Object.keys(parts).length > 0 ? { parts } : {}),
  };
  return new Blob([JSON.stringify(file)], { type: 'application/json' });
}

/** Parse a .novacad file and restore any missing assets. Throws on bad input. */
export async function restoreProjectFile(
  text: string,
): Promise<{ name: string; timeline: TimelineJSON }> {
  let parsed: NovacadFile;
  try {
    parsed = JSON.parse(text) as NovacadFile;
  } catch {
    throw new Error('Not a valid .novacad file (unparseable JSON)');
  }
  if (parsed.format !== 'novacad' || parsed.version !== 1 || !parsed.timeline) {
    throw new Error('Not a valid .novacad file');
  }
  for (const [hash, asset] of Object.entries(parsed.assets ?? {})) {
    if (await db.assets.get(hash)) continue; // content-addressed: already here
    const data = await base64ToBlob(asset.data, asset.mimeType);
    await db.assets.put({
      hash,
      kind: asset.kind ?? 'mesh', // old files carried only mesh assets
      mimeType: asset.mimeType,
      name: asset.name,
      byteLength: data.size,
      data,
      createdAt: Date.now(),
    });
  }
  // Restore embedded assembly sources under their ORIGINAL ids (part links
  // are by id) — existing projects with those ids are left untouched.
  for (const [pid, part] of Object.entries(parsed.parts ?? {})) {
    if (await db.projects.get(pid)) continue;
    const now = Date.now();
    await db.projects.put({
      id: pid,
      name: part.name,
      createdAt: now,
      updatedAt: now,
      timeline: part.timeline,
    });
  }
  return { name: parsed.name || 'Imported project', timeline: parsed.timeline };
}
